import { useState } from 'react';
import { BellOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AppSheet } from '@/components/ui/app-sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface SilenceAlertsModalProps {
  isActive: boolean;
  onClose: () => void;
  onSilence: (silenceUntil: string | undefined, reason: string) => void;
  isPending: boolean;
}

const DURATION_PRESETS = [
  { label: '1 hour', hours: 1 },
  { label: '4 hours', hours: 4 },
  { label: '8 hours', hours: 8 },
  { label: '24 hours', hours: 24 },
  { label: '3 days', hours: 72 },
  { label: '7 days', hours: 168 },
  { label: 'Indefinite', hours: 0 },
];

/**
 * SilenceAlertsModal — duration + reason form for bulk-silencing
 * alerts on a target (device, region, etc.). Used by DeviceDetails,
 * RegionDetails, and AlertDetails.
 *
 * Migrated to AppSheet (Sheet at mobile, Dialog at md+). Submission
 * yields silenceUntil ISO string (or undefined for indefinite) and
 * the reason text.
 */
export default function SilenceAlertsModal({
  isActive,
  onClose,
  onSilence,
  isPending,
}: SilenceAlertsModalProps) {
  const [selectedHours, setSelectedHours] = useState(24);
  const [reason, setReason] = useState('');

  const handleSubmit = () => {
    let silenceUntil: string | undefined;
    if (selectedHours > 0) {
      const until = new Date();
      until.setHours(until.getHours() + selectedHours);
      silenceUntil = until.toISOString();
    }
    onSilence(silenceUntil, reason);
  };

  const handleClose = () => {
    setSelectedHours(24);
    setReason('');
    onClose();
  };

  return (
    <AppSheet
      open={isActive}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title="Silence alerts"
      description="Silence all alert rules for this target. Existing open incidents will not be affected."
    >
      <AppSheet.Body>
        <form
          id="silence-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="silence-duration">Duration</Label>
            <Select
              value={String(selectedHours)}
              onValueChange={(v) => { setSelectedHours(parseInt(v, 10)); }}
            >
              <SelectTrigger id="silence-duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_PRESETS.map((p) => (
                  <SelectItem key={p.hours} value={String(p.hours)}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="silence-reason">Reason (optional)</Label>
            <Textarea
              id="silence-reason"
              rows={2}
              value={reason}
              onChange={(e) => { setReason(e.target.value); }}
              placeholder="e.g., Device under maintenance"
            />
          </div>
        </form>
      </AppSheet.Body>
      <AppSheet.Footer>
        <Button variant="outline" onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="silence-form"
          disabled={isPending}
          className="bg-sev-3 hover:bg-sev-3/90 text-foreground"
        >
          <BellOff className="w-4 h-4" />
          {isPending ? 'Silencing…' : 'Silence'}
        </Button>
      </AppSheet.Footer>
    </AppSheet>
  );
}
